/* Editorial Layout — the gold standard per-tenant template.

   Differs from the founder PublicLayout: this is a per-tenant template
   designed for any photographer who picks "Editorial" in their site
   settings. It applies the tenant's accent + typeface CSS variables,
   anchors a Lumetier signature in the corner, and stamps an IssueSeal
   into the top-right of the masthead. */

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme, getSeason } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";
import { Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function EditorialTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const season = getSeason();
  const year = new Date().getFullYear();

  return (
    <div
      className="min-h-screen antialiased selection:bg-[color:var(--tenant-accent)]"
      style={{
        fontFamily: "var(--tenant-sans)",
        background: "var(--tenant-bg, #0A0908)",
        color: "var(--tenant-fg, #F5F2EC)",
      }}
      data-template="editorial"
    >
      {/* ── Masthead ─────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled ? "backdrop-blur-md border-b" : ""
        }`}
        style={scrolled ? {
          background: "color-mix(in srgb, var(--tenant-bg, #0A0908) 90%, transparent)",
          borderBottomColor: "var(--tenant-fg-line, rgba(245,242,236,0.10))",
        } : { background: "transparent" }}
        data-testid="editorial-tenant-nav"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between gap-6">
          <Link
            to={tenantHref("/")}
            className="text-[11px] uppercase tracking-[0.32em] hover:opacity-70 transition-opacity"
            style={{ color: "var(--tenant-fg, #F5F2EC)" }}
            data-testid="editorial-tenant-wordmark"
          >
            <TenantWordmark logoUrl={settings?.logo_url} coverLogoUrl={settings?.cover_logo_url} tone="dark" studioName={studioName} imgClassName="h-8 w-auto">
              {studioName}
            </TenantWordmark>
          </Link>
          <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
            {NAV.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`editorial-tenant-nav-${it.label.toLowerCase()}`}
                className="text-[11px] uppercase tracking-[0.28em] transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? "var(--tenant-fg, #F5F2EC)" : "var(--tenant-fg-soft, rgba(245,242,236,0.55))",
                })}
              >
                {it.label}
              </NavLink>
            ))}
          </nav>
          <Link
            to={tenantHref("/booking")}
            className="hidden md:inline-flex items-center gap-2 px-5 h-10 text-[10px] uppercase tracking-[0.28em] font-medium transition-colors hover:opacity-90"
            style={{
              background: "var(--tenant-fg, #F5F2EC)",
              color: "var(--tenant-bg, #0A0908)",
            }}
            data-testid="editorial-tenant-book-cta"
          >
            Book a sitting →
          </Link>
          <TenantMobileNav tone="dark" ctaLabel="Book a sitting" studioName={studioName} />
        </div>
        {scrolled && <div className="h-px opacity-70" style={{ background: "var(--tenant-accent)" }} />}
      </header>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <main key={location.pathname}>
        <Outlet />
      </main>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer
        className="border-t py-16"
        style={{
          background: "var(--tenant-bg, #0A0908)",
          color: "var(--tenant-fg, #F5F2EC)",
          borderTopColor: "var(--tenant-fg-line, rgba(245,242,236,0.10))",
        }}
        data-testid="editorial-tenant-footer"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "var(--tenant-accent)" }}>Studio</div>
            <div className="text-3xl font-light leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontStyle: "italic" }}>
              {studioName}
            </div>
            {settings?.tagline ? (
              <div className="mt-3 text-sm italic" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
                {settings.tagline}
              </div>
            ) : null}
          </div>
          <div className="md:text-center">
            <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "var(--tenant-accent)" }}>Contact</div>
            <div className="text-sm space-y-1" style={{ color: "var(--tenant-fg-soft)" }}>
              {settings?.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.95 }}>
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.25} /> {settings.contact_email}
                </a>
              )}
              {settings?.contact_location && (
                <div className="italic" style={{ fontFamily: "var(--tenant-serif)" }}>{settings.contact_location}</div>
              )}
            </div>
          </div>
          <div className="md:text-right">
            <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "var(--tenant-accent)" }}>Follow</div>
            <div className="flex md:justify-end gap-4" style={{ color: "var(--tenant-fg-soft)" }}>
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100" style={{ opacity: 0.85 }}>
                  <Instagram className="w-4 h-4" strokeWidth={1.25} />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 mt-10 pt-6 border-t flex items-center justify-between text-[10px] uppercase tracking-[0.32em]"
             style={{ borderTopColor: "var(--tenant-fg-line)", color: "var(--tenant-fg-mute)" }}>
          <span>© {year} {studioName}</span>
          <LumetierFooterCredit className="hidden sm:inline" testid="editorial-lumetier-credit" />
          <span className="italic normal-case tracking-normal text-sm" style={{ fontFamily: "var(--tenant-serif)" }}>Issue Nº 01 · {season} {year}</span>
        </div>
      </footer>

      <TenantSignature tone="dark" />
    </div>
  );
}
